/**
 * Server-tick estimation (docs §3 "Client timing"). Ping/pong round trips (online) or
 * snapshot arrivals (offline worker, ~zero latency) feed an EMA of
 *   offset = serverTick − localMs / tickMs.
 * `now()` returns a smooth, monotonic fractional tick: small errors are slewed out, large
 * ones (tab sleep, server hiccup) snap.
 */
const EMA_ALPHA = 0.15;
/** max slew correction, ticks per tick of elapsed time (±12% speed) */
const MAX_SLEW = 0.12;
/** errors larger than this (ticks) snap instead of slewing */
const SNAP_TICKS = 6;

export class TickClock {
  private offset: number | null = null;
  private lastEst = 0;
  private lastT: number | null = null;
  private floorTick = 0;

  constructor(private readonly tickMs = 100) {}

  /** Forget the estimate (after a pause): the next sample starts it again. */
  reset(): void {
    this.offset = null;
    this.lastT = null;
  }

  /** A ping sent at local t0 came back at t1 carrying the server tick at reply time. */
  onPong(t0: number, t1: number, serverTick: number): void {
    const rtt = Math.max(0, t1 - t0);
    this.sample(serverTick + rtt / 2 / this.tickMs, t1);
  }

  /**
   * A snapshot for `tick` arrived at local time t. With `trustLatency` (offline worker) it is
   * used as a clock sample; otherwise it only bounds the estimate from below.
   */
  onSnapshot(tick: number, t: number, trustLatency: boolean): void {
    this.floorTick = Math.max(this.floorTick, tick);
    if (trustLatency || this.offset === null) this.sample(tick, t);
  }

  private sample(tickAtT: number, t: number): void {
    const o = tickAtT - t / this.tickMs;
    if (this.offset === null || Math.abs(o - this.offset) > SNAP_TICKS) this.offset = o;
    else this.offset += EMA_ALPHA * (o - this.offset);
  }

  private raw(t: number): number {
    return t / this.tickMs + (this.offset ?? 0);
  }

  /** Smoothed estimate of the server's current (fractional) tick at local time t (ms). */
  now(t: number): number {
    if (this.offset === null) return this.floorTick;
    const target = Math.max(this.raw(t), this.floorTick - 1);
    if (this.lastT === null) {
      this.lastT = t;
      this.lastEst = target;
      return target;
    }
    const dTicks = Math.max(0, (t - this.lastT) / this.tickMs);
    const predicted = this.lastEst + dTicks;
    const err = target - predicted;
    let est: number;
    if (Math.abs(err) > SNAP_TICKS) est = target;
    else {
      const lim = MAX_SLEW * dTicks;
      est = Math.max(this.lastEst, predicted + Math.max(-lim, Math.min(lim, err)));
    }
    this.lastT = t;
    this.lastEst = est;
    return est;
  }
}
